import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRouteNames, SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { usePromoteChannels } from '../promote/hooks/usePromoteChannels';
import { useSocialPeriodPosts } from '../promote/hooks/useSocialPeriodPosts';
import { channelHasSocialNumbers, channelNumbersState } from '../promote/channels/channelNumbersState';
import { SerializedSocialPost } from '../promote/types';
import { SocialPostDetailModal } from './SocialPostDetailModal';
import { socialPostPath } from './socialPostPath';
import { SocialPostsEmpty } from './SocialPostsEmpty';
import { SocialPostsPanel } from './SocialPostsPanel';
import { SocialTotalsRow } from './SocialTotalsRow';
import { SocialReconnectLines } from './SocialReconnectLines';

interface SocialNumbersSectionProps {
  accountId: number;
  /** The period in days. */
  days: number;
  /** The post that `/social/post/:id` opens. Undefined: the detail is closed. */
  openPostId?: number;
}

/**
 * The read side of Social: the Reconnect lines, the totals row per channel, the posts table and
 * the post detail. With no connected channel it shows "No channel connected yet" with a link to
 * Channels. The screen has no connect control (design your-intelligence g). The totals and the
 * table load on their own.
 */
const SocialNumbersSectionComponent: React.FC<SocialNumbersSectionProps> = ({ accountId, days, openPostId }) => {
  const router = useSMLRouter();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { channels, isLoading, error, retry } = usePromoteChannels({ accountId });
  // The row that the person clicked: the head and the tiles show at once from it.
  const [clickedPost, setClickedPost] = useState<SerializedSocialPost>();

  // A channel that is not connected has no totals card and no posts.
  const connected = useMemo(() => channels.filter(channelHasSocialNumbers), [channels]);
  const signedOut = useMemo(
    () => connected.filter((channel) => channelNumbersState(channel) === 'reconnect'),
    [connected]
  );
  const platforms = useMemo(() => connected.map((channel) => channel.platform), [connected]);
  const hasChannels = connected.length > 0;
  const periodPosts = useSocialPeriodPosts({ accountId, days, skip: isLoading || !hasChannels });

  const openChannels = useCallback(() => router.push(SMLRoutes.promoteChannels), [router]);
  const openPost = useCallback(
    (post: SerializedSocialPost) => {
      setClickedPost(post);
      // eslint-disable-next-line no-void
      void navigate(`${socialPostPath(post.id)}${search}`);
    },
    [navigate, search]
  );
  const closePost = useCallback(() => {
    // eslint-disable-next-line no-void
    void navigate(`/${SMLRouteNames.social}${search}`);
  }, [navigate, search]);

  if (isLoading) {
    return <LoadingView />;
  }
  if (error !== undefined) {
    return <ErrorView error={error} retry={retry} />;
  }
  if (!hasChannels) {
    return <SocialPostsEmpty kind="noChannel" onAction={openChannels} />;
  }

  return (
    <>
      <SocialReconnectLines channels={signedOut} onOpenChannels={openChannels} />
      {/* A sum of some pages is not a total, so a page that fails gives the error state. */}
      {periodPosts.error === undefined ? (
        <SocialTotalsRow channels={connected} posts={periodPosts.posts} isCapped={periodPosts.isCapped} />
      ) : (
        <ErrorView error={periodPosts.error} retry={periodPosts.retry} />
      )}
      <SocialPostsPanel
        accountId={accountId}
        days={days}
        platforms={platforms}
        openPostId={openPostId}
        onOpen={openPost}
      />
      <SocialPostDetailModal
        accountId={accountId}
        postId={openPostId}
        post={clickedPost?.id === openPostId ? clickedPost : undefined}
        days={days}
        onClose={closePost}
      />
    </>
  );
};

export const SocialNumbersSection = TypedMemo(SocialNumbersSectionComponent);
